import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { api } from "../../lib/api";

const EMPTY = { name: "", origin: "", text_fr: "", text_en: "", rating: 5, published: true };

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/testimonials").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (form.id && !String(form.id).startsWith("seed-")) {
        await api.put(`/admin/testimonials/${form.id}`, form);
      } else if (form.id) {
        await api.put(`/admin/testimonials/${form.id}`, form);
      } else {
        await api.post("/admin/testimonials", form);
      }
      toast.success("Témoignage enregistré");
      setForm(null);
      load();
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce témoignage ?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    load();
  };

  if (form) {
    return (
      <div data-testid="testimonial-form">
        <h1 className="font-serif text-3xl text-ink">{form.id ? "Modifier" : "Nouveau"} témoignage</h1>
        <div className="w-16 h-px bg-gold mt-4" />
        <form onSubmit={save} className="mt-8 bg-ivory border border-champagne rounded-2xl p-7 max-w-2xl space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <input data-testid="tm-name" required className="input-elegant" placeholder="Nom" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="tm-origin" className="input-elegant" placeholder="Origine (ex : Paris)" value={form.origin}
              onChange={(e) => setForm({ ...form, origin: e.target.value })} />
          </div>
          <textarea data-testid="tm-text-fr" required rows={4} className="input-elegant" placeholder="Témoignage (FR)" value={form.text_fr}
            onChange={(e) => setForm({ ...form, text_fr: e.target.value })} />
          <textarea data-testid="tm-text-en" rows={4} className="input-elegant" placeholder="Testimonial (EN)" value={form.text_en}
            onChange={(e) => setForm({ ...form, text_en: e.target.value })} />
          <div className="flex items-center gap-6">
            <label className="text-sm text-earth flex items-center gap-2">Note
              <select data-testid="tm-rating" className="input-elegant !w-auto !py-2" value={form.rating}
                onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })}>
                {[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>
            <label className="text-sm text-earth flex items-center gap-2">
              <input data-testid="tm-published" type="checkbox" checked={form.published}
                onChange={(e) => setForm({ ...form, published: e.target.checked })} className="accent-terracotta w-4 h-4" />
              Publié
            </label>
          </div>
          <div className="flex gap-3">
            <button data-testid="tm-save-btn" type="submit" className="btn-primary !py-3">Enregistrer</button>
            <button data-testid="tm-cancel-btn" type="button" onClick={() => setForm(null)} className="btn-outline !py-3">Annuler</button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-testimonials">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl text-ink">Témoignages</h1>
          <div className="w-16 h-px bg-gold mt-4" />
        </div>
        <button data-testid="new-testimonial-btn" onClick={() => setForm({ ...EMPTY })} className="btn-primary !py-3">
          <Plus size={16} /> Nouveau témoignage
        </button>
      </div>
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((tm) => (
          <div key={tm.id} className="bg-ivory border border-champagne rounded-2xl p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-serif text-xl text-ink">{tm.name}</h3>
                <p className="text-xs text-earth">{tm.origin} · {"★".repeat(tm.rating)} {tm.published ? "· Publié" : "· Masqué"}</p>
              </div>
              <div className="flex gap-2">
                <button data-testid={`edit-tm-${tm.id}`} onClick={() => setForm({ ...EMPTY, ...tm })}
                  className="w-9 h-9 rounded-full border border-champagne flex items-center justify-center text-earth hover:border-terracotta hover:text-terracotta transition-colors">
                  <Pencil size={14} />
                </button>
                <button data-testid={`delete-tm-${tm.id}`} onClick={() => remove(tm.id)}
                  className="w-9 h-9 rounded-full border border-champagne flex items-center justify-center text-earth hover:border-burgundy hover:text-burgundy transition-colors">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <p className="text-sm text-earth font-light mt-3 italic">“{tm.text_fr}”</p>
          </div>
        ))}
      </div>
    </div>
  );
}
